import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import axiosInstance from "../../utils/axios";
import { entitiesConfig } from "../../components/templateComponents/constants";
import "./BeaconTable.css";


const BeaconTable = ({
  markers = [],
  onAddRow,
  onUpdateMarker,
  onDeleteMarker,
  availableBeaconsCount,
}) => {
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState({});
  const [wayterId, setWayterId] = useState("");
  const [becEstId, setBecEstId] = useState("");
  const [reserved, setReserved] = useState("0000");
  const [saving, setSaving] = useState(false);

  const entityConfig = entitiesConfig.beaconTable;

  useEffect(() => {
    const fetchWayternId = async () => {
      try {
        const response = await axiosInstance.get(entityConfig.fetchWayternId);
        if (response.data) setWayterId(response.data);
      } catch (error) {
        console.error("Error fetching waytern_id:", error);
      }
    };
    fetchWayternId();
  }, []);

  useEffect(() => {
    const fetchBecEstId = async () => {
      const campusId = localStorage.getItem("selectedCampusId");
      if (!campusId) {
        console.warn("No campusId in localStorage");
        return;
      }
      try {
        const response = await axiosInstance.post(entityConfig.viewBuilding, {
          view_type: "view all",
          campusid: campusId,
          bec_map: "campus",
        });
        if (
          response.data?.Status === "Success" &&
          response.data.Data.length > 0
        ) {
          setBecEstId(response.data.Data[0].bec_est_id);
        }
      } catch (error) {
        console.error("Error fetching bec_est_id:", error);
      }
    };
    fetchBecEstId();
  }, []);

  const computeNamespace = () => {
    const estPadded = String(becEstId).padStart(12, "0");
    const reservedPadded = String(reserved).padStart(4, "0");
    return `${wayterId}${estPadded}${reservedPadded}`;
  };

  const computeInstance = (marker) => {
    const flr_id = String(marker.bec_floor_id || "").padStart(2, "0");
    const type_id = String(marker.bec_typ_id || "").padStart(2, "0");
    const x = String(marker.bex_pt_map_X || "").padStart(4, "0");
    const y = String(marker.bex_pt_map_Y || "").padStart(4, "0");
    return `${flr_id}${type_id}${x}${y}`;
  };

  const computeUniqueId = (marker) => {
    const ns = computeNamespace();
    const ins = computeInstance(marker);
    return `0x${ns}${ins}`;
  };

  const namespace = computeNamespace();

  const handleEdit = (id) => {
    const marker = markers.find((m) => m.id === id);
    if (!marker) return;
    setEditingId(id);
    setEditValue({
      points: marker.points || "",
      bex_pt_map_X: marker.bex_pt_map_X || "",
      bex_pt_map_Y: marker.bex_pt_map_Y || "",
    });
  };

  const handleChange = (field, value) => {
    setEditValue((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = (id) => {
    if (!onUpdateMarker) return;
    // Call onUpdateMarker for each field that was edited
    onUpdateMarker(id, "points", editValue.points);
    onUpdateMarker(id, "bex_pt_map_X", editValue.bex_pt_map_X);
    onUpdateMarker(id, "bex_pt_map_Y", editValue.bex_pt_map_Y);
    setEditingId(null);
    setEditValue({});
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditValue({});
  };

  const handleAddRow = () => {
    if (onAddRow) {
      const newRow = onAddRow();
      if (!newRow) return;
      setEditingId(newRow.id);
      setEditValue({
        points: "",
        bex_pt_map_X: "",
        bex_pt_map_Y: "",
      });
    }
  };

  const handleDownload = () => {
    const tableData = markers.map((marker) => ({
      "Marker ID": marker.id,
      bec_hex_identifier: marker.bec_hex_identifier,
      bec_floor_id: marker.bec_floor_id,
      bec_typ_id: marker.bec_typ_id || "N/A",
      bex_pt_map_X: marker.bex_pt_map_X,
      bex_pt_map_Y: marker.bex_pt_map_Y,
      wayter_id: wayterId,
      bec_est_id: becEstId,
      reserved: reserved || "0000",
      bec_namespace_key: namespace,
      bec_instance_key: computeInstance(marker),
      bec_unique_id: computeUniqueId(marker),
      bec_name: marker.bec_name || "N/A",
    }));
    const worksheet = XLSX.utils.json_to_sheet(tableData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "BeaconData");
    XLSX.writeFile(workbook, "BeaconData.xlsx");
  };

  // const handleSaveToDB = async () => {
  //   setSaving(true);
  //   const promises = markers.map(async (marker) => {
  //     const payload = {
  //       bec_name: marker.bec_name || "N/A",
  //       waytern_id: wayterId,
  //       bldg_id: 1,
  //       bec_floor_id: marker.bec_floor_id,
  //       bec_type_id: marker.bec_typ_id,
  //       bex_pt_map_X: marker.bex_pt_map_X,
  //       bex_pt_map_Y: marker.bex_pt_map_Y,
  //       point: marker.points || "G09",
  //     };

  //     try {
  //       const response = await axiosInstance.post(
  //         "https://api.waytern.in/registration_beacon_config/v1.1",
  //         payload
  //       );
  //       return response.data;
  //     } catch (error) {
  //       console.error("Error saving beacon to DB:", error);
  //       return null;
  //     }
  //   });

  //   const results = await Promise.all(promises);
  //   console.log("Save to DB results:", results);
  //   setSaving(false);
  // };

const handleSaveToDB = async () => {
    setSaving(true);
    try {
      const results = await Promise.all(
        markers.map(async (marker) => {
          const payload = {
            bec_name: marker.bec_name || "N/A",
            waytern_id: wayterId,
            bldg_id: 1,
            bec_floor_id: marker.bec_floor_id,
            bec_type_id: marker.bec_typ_id,
            bex_pt_map_X: marker.bex_pt_map_X,
            bex_pt_map_Y: marker.bex_pt_map_Y,
            point: marker.points || "G09",
          };

          const response = await axiosInstance.post(entityConfig.saveEndpoint, payload);
          return response.data;
        })
      );
      console.log("Save to DB results:", results);
    } catch (error) {
      console.error("Error saving beacons to DB:", error);
      alert("Error saving to DB.");
    } finally {
      setSaving(false);
    }
  };


  if (!markers || markers.length === 0) {
    return (
      <div className="beacon-table-empty">
        <p>No beacons plotted yet. Click on the image to add beacons.</p>
      </div>
    );
  }

  return (
    <div className="beacon-table-container">
      <div className="beacon-table-header">
        <h3 className="beacon-table-title">Beacon Data Table</h3>
        <div className="beacon-table-actions">
          <button
            onClick={handleAddRow}
            className="add-beacon-btn"
            disabled={markers.length >= availableBeaconsCount}
            title={
              markers.length >= availableBeaconsCount
                ? "Beacon limit reached"
                : "Add New Beacon"
            }
          >
            <span className="icon-plus" /> Add Beacon
          </button>
          <button
            onClick={handleDownload}
            className="download-excel-btn"
            title="Download as Excel"
          >
            <span className="icon-download" />
          </button>
        </div>
      </div>

      <div className="beacon-table-scroll">
        <table className="beacon-table">
          <thead>
            <tr>
              <th>Marker ID</th>
              <th>bec_hex_identifier</th>
              <th>bec_floor_id</th>
              <th>bec_typ_id</th>
              <th>bex_pt_map_X</th>
              <th>bex_pt_map_Y</th>
              <th>wayter_id</th>
              <th>bec_est_id</th>
              <th>reserved</th>
              <th>bec_namespace_key</th>
              <th>bec_instance_key</th>
              <th>bec_unique_id</th>
              <th>points</th>
              <th>Beacon Name</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {markers.map((marker) => (
              <tr key={marker.id} className="beacon-table-row">
                <td>{marker.id}</td>
                <td>{marker.bec_hex_identifier}</td>
                <td>{marker.bec_floor_id}</td>
                <td>{marker.bec_typ_id}</td>
                <td>
                  {editingId === marker.id ? (
                    <input
                      type="number"
                      value={editValue.bex_pt_map_X}
                      onChange={(e) =>
                        handleChange("bex_pt_map_X", e.target.value)
                      }
                      className="cell-input"
                    />
                  ) : (
                    <span onClick={() => handleEdit(marker.id)}>
                      {marker.bex_pt_map_X}
                    </span>
                  )}
                </td>
                <td>
                  {editingId === marker.id ? (
                    <input
                      type="number"
                      value={editValue.bex_pt_map_Y}
                      onChange={(e) =>
                        handleChange("bex_pt_map_Y", e.target.value)
                      }
                      className="cell-input"
                    />
                  ) : (
                    <span onClick={() => handleEdit(marker.id)}>
                      {marker.bex_pt_map_Y}
                    </span>
                  )}
                </td>
                <td>{wayterId}</td>
                <td>{becEstId}</td>
                <td>{reserved}</td>
                <td>{namespace}</td>
                <td>{computeInstance(marker)}</td>
                <td>{computeUniqueId(marker)}</td>
                <td>
                  {editingId === marker.id ? (
                    <input
                      type="text"
                      value={editValue.points}
                      onChange={(e) => handleChange("points", e.target.value)}
                      className="cell-input"
                    />
                  ) : (
                    <span onClick={() => handleEdit(marker.id)}>
                      {marker.points}
                    </span>
                  )}
                </td>
                <td>{marker.bec_name || "N/A"}</td>
                <td>
                  {editingId === marker.id ? (
                    <div className="action-group">
                      <button
                        onClick={() => handleSave(marker.id)}
                        className="action-btn save"
                        title="Save"
                      >
                        ✔
                      </button>
                      <button
                        onClick={handleCancel}
                        className="action-btn cancel"
                        title="Cancel"
                      >
                        ✖
                      </button>
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => handleEdit(marker.id)}
                        className="action-btn edit"
                        title="Edit points, X, Y"
                      >
                        ✎
                      </button>
                      <button
                        onClick={() =>
                          onDeleteMarker && onDeleteMarker(marker.id)
                        }
                        className="action-btn delete"
                        title="Delete"
                      >
                        🗑
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div
        className="action-group"
        style={{ marginTop: "16px", textAlign: "right" }}
      >
        <button
          onClick={handleSaveToDB}
          className="save-db-btn"
          disabled={saving}
          style={{
            background: "#10b981",
            color: "#fff",
            padding: "8px 16px",
            border: "none",
            borderRadius: "6px",
          }}
        >
          {saving ? "Saving..." : "Save to DB"}
        </button>
      </div>
    </div>
  );
};

export default BeaconTable;
